import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Get } from '../services/service.api';
import { toast } from 'react-toastify';
import { Building2, ChevronDown, Plus, Check } from 'lucide-react';

export default function CompanySwitcher() {
  const dispatch = useDispatch();
  const token = useSelector(s => s.userReducer.token);
  const activeCompany = useSelector(s => s.userReducer.activeCompany);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchCompanies = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      let res = await Get('/companies/mine', token);
      if (!Array.isArray(res) || res.length === 0) {
        // Fallback: si /mine ne retourne rien, essayer /companies
        const all = await Get('/companies', token);
        if (Array.isArray(all)) res = all;
      }
      if (Array.isArray(res)) {
        setCompanies(res);
        // Auto-select first if none active or active not in list
        if (res.length > 0) {
          const currentExists = activeCompany && res.some(c => c.id === activeCompany.id);
          if (!currentExists) {
            dispatch({ type: 'set-company', value: res[0] });
            localStorage.setItem('activeCompanyId', res[0].id);
          }
        }
      }
    } catch (err) {
      console.error('[CompanySwitcher] Error loading companies', err);
    } finally {
      setLoading(false);
    }
  }, [token, activeCompany, dispatch]);

  useEffect(() => {
    fetchCompanies();
    const handleRefresh = () => fetchCompanies();
    window.addEventListener('company-created', handleRefresh);
    return () => {
      window.removeEventListener('company-created', handleRefresh);
    };
  }, [fetchCompanies]);

  const handleSwitch = (company) => {
    dispatch({ type: 'set-company', value: company });
    localStorage.setItem('activeCompanyId', company.id);
    setOpen(false);
    toast.success(`🏢 Entreprise active : ${company.libelle}`, { autoClose: 2000 });
    // Reload page data by triggering a navigation re-render
    window.dispatchEvent(new Event('company-switched'));
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '6px 12px', borderRadius: 'var(--radius-sm)',
        background: 'var(--bg-input)', border: '1px solid var(--border)',
        fontSize: '0.8rem', color: 'var(--text-muted)',
      }}>
        <Building2 size={14} /> Chargement...
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Trigger button */}
      <button
        className="company-switcher-btn"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 12px', borderRadius: 'var(--radius-sm)',
          background: 'var(--bg-input)', border: '1px solid var(--border)',
          color: 'var(--text-primary)', cursor: 'pointer',
          fontSize: '0.82rem', fontWeight: 600,
          transition: 'var(--transition)',
        }}
        title="Changer d'entreprise"
      >
        <Building2 size={14} color="var(--primary)" />
        <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {activeCompany ? activeCompany.libelle : 'Choisir une entreprise'}
        </span>
        <ChevronDown size={13} style={{ flexShrink: 0, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="company-dropdown" style={{
          position: 'absolute', top: 'calc(100% + 8px)', left: 0,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          minWidth: 220, maxWidth: 300, zIndex: 1000,
          overflow: 'hidden',
          animation: 'fadeIn 0.15s ease',
        }}>
          {/* Company list */}
          {companies.length === 0 ? (
            <div style={{ padding: '12px 16px', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Aucune entreprise
            </div>
          ) : (
            <>
              <div style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                {companies.map(c => (
                  <button
                    key={c.id}
                    onClick={() => handleSwitch(c)}
                    style={{
                      width: '100%', textAlign: 'left',
                      padding: '10px 16px', border: 'none',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                      color: activeCompany?.id === c.id ? 'var(--primary)' : 'var(--text-primary)',
                      background: activeCompany?.id === c.id ? 'var(--primary-glow)' : 'transparent',
                      fontSize: '0.85rem', fontWeight: activeCompany?.id === c.id ? 700 : 400,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { if (activeCompany?.id !== c.id) e.currentTarget.style.background = 'var(--bg-input)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = activeCompany?.id === c.id ? 'var(--primary-glow)' : 'transparent'; }}
                  >
                    <Building2 size={13} style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.libelle}</span>
                    {activeCompany?.id === c.id && <Check size={13} />}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Limit badge + Create new */}
          <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
              {companies.length}/2 entreprises (Free)
              <span style={{ marginLeft: 6, color: '#f59e0b', fontWeight: 600 }}>
                · Pro (10 max) à venir 🚀
              </span>
            </div>
            <button
              onClick={() => { setOpen(false); window.location.href = '/companies'; }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 10px', borderRadius: 'var(--radius-sm)',
                background: 'var(--primary-glow)', border: '1px dashed var(--primary)',
                color: 'var(--primary)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                width: '100%',
              }}
            >
              <Plus size={13} /> Gérer mes entreprises
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
